import { v4 as uuidv4 } from 'uuid';

import { useAppDispatch, useAppSelector } from '@verantos-dev/app-state-base';
import { selectCohortDefinitionIsVersionLoading } from '@verantos-dev/app-state-cohort-definition';
import { useCohortBuilderData } from '@verantos-dev/data-access-cohorts';
import {
  CohortBuilderModalLegacy,
  CohortStatusLegacy,
  SubjectVariableLegacy,
} from '@verantos-dev/types-cohorts';

import { useCohortEditable } from './useCohortEditable';
import { deleteSubjectVariable, setModal } from '../cohortBuilderSlice';
import { useCohortBuilderItemWithInUseItems } from './useCohortBuilderItemWithInUseItems';
import { selectModal } from '../selectors/cohortBuilderWorkflowSelectors';
import {
  selectCohortDefinition,
  selectSubjectVariables,
} from '../selectors/cohortDefinitionSelectors';
import { getFullCohortDslForDeleteSubjectsVariables } from '../utils/dsl-generators/getFullCohortDslForSubjectsVariables';

type SubjectVariableWithInUseItems = SubjectVariableLegacy & {
  isInUse: boolean;
  inUseItems: string[] | undefined;
};

export const useCriteriaSectionView = () => {
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector(selectCohortDefinitionIsVersionLoading);
  const { data: cohortData } = useCohortBuilderData();
  const { isEditable } = useCohortEditable();
  const isProcessing =
    !!cohortData?.data.latestStatus &&
    [CohortStatusLegacy.PROCESSING, CohortStatusLegacy.FINAL].includes(
      cohortData?.data.latestStatus
    );
  const viewMode = !isEditable || isProcessing;

  /** Cohort Builder Workflow State */
  const modal = useAppSelector(selectModal);
  const currentCohortDefinition = useAppSelector(selectCohortDefinition);
  /** Cohort Builder Open Edit Modal */
  const onAddSubjectVariableClick = () => {
    dispatch(
      setModal({
        id: uuidv4(),
        modal: CohortBuilderModalLegacy.ADD_SUBJECT_VARIABLE,
      })
    );
  };

  /** Cohort Builder Definition State */
  const subjectVariables = useAppSelector(selectSubjectVariables);
  const subjectVariablesWithInUseItems = useCohortBuilderItemWithInUseItems(subjectVariables) as
    | SubjectVariableWithInUseItems[]
    | undefined;

  const onEditClick = (id: string) => {
    dispatch(setModal({ id, modal: CohortBuilderModalLegacy.EDIT_SUBJECT_VARIABLE }));
  };

  const onDeleteClick =
    (emitResearchAssistantUpdate: (nextCohortDsl: string) => void) => (id: string) => {
      if (!viewMode) {
        dispatch(deleteSubjectVariable(id));
        const nextCohortDsl = getFullCohortDslForDeleteSubjectsVariables(
          currentCohortDefinition,
          id
        );
        emitResearchAssistantUpdate(nextCohortDsl.fullDslQueryString);
      }
    };

  const showSubjectVariableEditModal =
    !!modal &&
    [
      CohortBuilderModalLegacy.ADD_SUBJECT_VARIABLE,
      CohortBuilderModalLegacy.EDIT_SUBJECT_VARIABLE,
    ].includes(modal);

  return {
    subjectVariables: subjectVariablesWithInUseItems,
    isEditable,
    isLoading,
    isProcessing,
    viewMode,

    showSubjectVariableEditModal,
    modal,

    onEditClick,
    onDeleteClick,
    onAddSubjectVariableClick,
  };
};
